import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import "./UserBooking.css"
import Header from "../components/Header"
import axios from "axios"

const getAuth = () =>
  document.cookie.replace(/(?:(?:^|.*;\s*)auth\s*=\s*([^;]*).*$)|^.*$/, "$1")

const TYPE_STYLES = {
  daily:      { bg: "rgba(239,68,68,0.12)",    color: "#f87171" },
  monthly:    { bg: "rgba(59,130,246,0.12)",   color: "#60a5fa" },
  quarterly:  { bg: "rgba(251,146,60,0.12)",   color: "#fb923c" },
  halfyearly: { bg: "rgba(139,92,246,0.12)",   color: "#c084fc" },
  yearly:     { bg: "rgba(16,185,129,0.12)",   color: "#34d399" },
}

const TYPE_LABELS = {
  daily: "1 Day", monthly: "1 Month", quarterly: "3 Months",
  halfyearly: "6 Months", yearly: "12 Months",
}

const UserBookingPage = () => {
  const location = useLocation()
  const params = new URLSearchParams(location.search)

  const [bookings, setBookings] = useState([])
  const [filteredBookings, setFilteredBookings] = useState([])
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [selectedSlotTime, setSelectedSlotTime] = useState("all")
  const [searchTerm, setSearchTerm] = useState(params.get("name") || "")
  const [totalRevenue, setTotalRevenue] = useState(0)

  const getAllBooking = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/booking`, {
        headers: { auth: getAuth() }
      })
      const data = response.data.Booking
      setBookings(data)
      setFilteredBookings(data)
    } catch (error) {
      console.error("Error fetching bookings:", error)
    }
  }

  useEffect(() => { getAllBooking() }, [])

  useEffect(() => {
    let filtered = bookings

    if (startDate && endDate) {
      filtered = filtered.filter(b => {
        const d = new Date(b.bookingDate)
        const s = new Date(startDate); s.setHours(0,0,0,0)
        const e = new Date(endDate);   e.setHours(23,59,59,999)
        return d >= s && d <= e
      })
    } else if (startDate) {
      filtered = filtered.filter(b => {
        const d = new Date(b.bookingDate)
        const s = new Date(startDate); s.setHours(0,0,0,0)
        return d >= s
      })
    } else if (endDate) {
      filtered = filtered.filter(b => {
        const d = new Date(b.bookingDate)
        const e = new Date(endDate); e.setHours(23,59,59,999)
        return d <= e
      })
    }

    if (selectedSlotTime !== "all") {
      filtered = filtered.filter(b => b.slotStartTime === selectedSlotTime)
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      filtered = filtered.filter(b =>
        b.userFullName?.toLowerCase().includes(q) ||
        b.userMobileNumber?.includes(searchTerm)
      )
    }

    const revenue = filtered
      .filter(b => b.isPaid)
      .reduce((sum, b) => sum + Number(b.subscriptionPrice), 0)

    setTotalRevenue(revenue)
    setFilteredBookings(filtered)
  }, [startDate, endDate, selectedSlotTime, searchTerm, bookings])

  const handleRefresh = () => {
    setStartDate("")
    setEndDate("")
    setSelectedSlotTime("all")
    getAllBooking()
  }

  const uniqueSlots = [...new Set(bookings.map(b => b.slotStartTime).filter(Boolean))]

  const getTypeStyle = (type) => TYPE_STYLES[type?.toLowerCase()] || { bg: "rgba(255,255,255,0.06)", color: "#94a3b8" }
  const getTypeLabel = (type) => TYPE_LABELS[type?.toLowerCase()] || type

  return (
    <div className="ubk-page">
      <Header />
      <div className="ubk-content">

        {/* Page Header */}
        <div className="ubk-page-header">
          <div>
            <h1 className="ubk-page-title">Bookings</h1>
            <p className="ubk-page-sub">View and filter all customer bookings</p>
          </div>
          <div className="ubk-header-right">
            <div className="ubk-revenue-badge">
              Revenue: <span>₹{totalRevenue.toLocaleString()}</span>
            </div>
            <button className="ubk-btn-ghost" onClick={handleRefresh}>↺ Refresh</button>
          </div>
        </div>

        {/* Filters */}
        <div className="ubk-filters-bar">
          <div className="ubk-filter-group">
            <label>Start Date</label>
            <input
              type="date"
              className="ubk-filter-input"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </div>
          <div className="ubk-filter-group">
            <label>End Date</label>
            <input
              type="date"
              className="ubk-filter-input"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </div>
          <div className="ubk-filter-group">
            <label>Slot Time</label>
            <select
              className="ubk-filter-input"
              value={selectedSlotTime}
              onChange={e => setSelectedSlotTime(e.target.value)}
            >
              <option value="all">All Slots</option>
              {uniqueSlots.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="ubk-filter-group">
            <label>Search</label>
            <input
              type="text"
              className="ubk-filter-input"
              placeholder="Name or mobile number"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="ubk-card">
          <div className="ubk-card-head">
            <h2>Booking Records</h2>
            <span className="ubk-count-badge">{filteredBookings.length} records</span>
          </div>
          <div className="ubk-table-wrap">
            {filteredBookings.length === 0 ? (
              <div className="ubk-empty">
                <div className="ubk-empty-icon">📋</div>
                <p>No bookings found for the selected filters</p>
              </div>
            ) : (
              <table className="ubk-table">
                <thead>
                  <tr>
                    <th>Booking ID</th>
                    <th>Customer</th>
                    <th>Mobile</th>
                    <th>Date</th>
                    <th>Slot</th>
                    <th>Price</th>
                    <th>Type</th>
                    <th>Rating</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((b, i) => {
                    const style = getTypeStyle(b.bookingType)
                    return (
                      <tr key={b.bookingId || i}>
                        <td><span className="ubk-id">{b.bookingId}</span></td>
                        <td><span className="ubk-name">{b.userFullName}</span></td>
                        <td>{b.userMobileNumber}</td>
                        <td>{new Date(b.bookingDate).toLocaleDateString()}</td>
                        <td>{b.slotStartTime || '—'}</td>
                        <td>₹{Number(b.subscriptionPrice).toLocaleString()}</td>
                        <td>
                          <span
                            className="ubk-type-badge"
                            style={{ background: style.bg, color: style.color }}
                          >
                            {getTypeLabel(b.bookingType)}
                          </span>
                        </td>
                        <td>{b.gymRating ? `${b.gymRating} ★` : '—'}</td>
                        <td>
                          <span className={`ubk-paid-badge ${b.isPaid ? 'paid' : 'unpaid'}`}>
                            {b.isPaid ? 'Paid' : 'Unpaid'}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

      </div>
    </div>
  )
}

export default UserBookingPage
